import { useEffect, useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Image,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { MenuProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  OrderedListOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@/lib/supabase'
import {
  AnimeHiatusAction,
  isAnimeHiatusActive,
} from '@/components/AnimeHiatusAction'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { useIsMobile } from '@/lib/hooks'
import { formatCST } from '@/lib/time'
import { ANIME_CACHE_TAGS, revalidateNextTag } from '@/lib/revalidate'
import {
  ANIME_LIST_STATE_STORAGE_KEY,
  hasAnimeListSearch,
  parseAnimeListSearch,
  parseStoredAnimeListState,
  serializeAnimeListSearch,
  toStoredAnimeListState,
  validateAnimeListSearch,
} from './-anime-list-state'
import type {
  AnimeListFilters as Filters,
  AnimeListSortField as SortField,
  AnimeListSortOrder as SortOrder,
  AnimeListState,
} from './-anime-list-state'

export const Route = createFileRoute('/_authed/anime')({
  validateSearch: validateAnimeListSearch,
  component: AnimePage,
})

type AnimeRow = Tables<'animes'> & {
  anime_types: { id: number; name: string } | null
  anime_series: { id: number; title: string } | null
}

const PAGE_SIZE = 50
// 批量删除上限比其它批量动作更严（PAGE_SIZE=50 天然落在两条上限内）
const BULK_DELETE_CAP = 100

/* ── 热度（admin.get_anime_heat：日级快照，非实时） ─────────── */

interface AnimeHeat {
  anime_id: number
  views_yesterday: number
  views_7d: number
  views_prev_7d: number
  views_30d: number
}

interface AnimeHeatResult {
  ok: boolean
  error?: string
  as_of?: string | null
  items?: AnimeHeat[]
}

const EMPTY_HEAT: Omit<AnimeHeat, 'anime_id'> = {
  views_yesterday: 0,
  views_7d: 0,
  views_prev_7d: 0,
  views_30d: 0,
}

function formatCount(n: number): string {
  if (n >= 100_000_000) return `${(n / 100_000_000).toFixed(1)}亿`
  if (n >= 10_000) return `${(n / 10_000).toFixed(1)}万`
  return n.toLocaleString('zh-CN')
}

// 周环比：两周都很小时波动没意义，不显示箭头
function weekOverWeek(cur: number, prev: number) {
  if (cur + prev < 20) return null
  if (prev === 0) return { text: '新', up: true }
  const pct = Math.round(((cur - prev) / prev) * 100)
  if (pct === 0) return null
  return { text: `${pct > 0 ? '↑' : '↓'}${Math.abs(pct)}%`, up: pct > 0 }
}

type HeatSortField = 'views_yesterday' | 'views_7d' | 'views_30d'

function isHeatSortField(field: string): field is HeatSortField {
  return (
    field === 'views_yesterday' || field === 'views_7d' || field === 'views_30d'
  )
}

interface HeatSortResult {
  ok: boolean
  error?: string
  total?: number
  ids?: number[]
}

/* ── 批量 RPC 返回形状 ─────────────────────────────────────── */

interface BulkFinishResult {
  ok: boolean
  error?: string
  max?: number
  batch_id?: string
  requested?: number
  changed?: number
  finished?: number[]
  skipped?: Array<{ id: number; reason: string }>
}

interface BulkFlagsResult {
  ok: boolean
  error?: string
  max?: number
  batch_id?: string
  requested?: number
  changed?: number
  not_found?: number[]
  unchanged?: number[]
}

interface CascadeCounts {
  favorites?: number
  comments?: number
  play_progress?: number
  episode_feedback?: number
}

interface BulkDeleteEntry {
  id: number
  title?: string
  error?: string
  episode_count?: number
  play_url_count?: number
  redirect_count?: number
  counts?: CascadeCounts
  cascaded?: CascadeCounts
}

interface BulkDeleteResult {
  ok: boolean
  error?: string
  max?: number
  dry_run?: boolean
  batch_id?: string
  requested?: number
  eligible?: BulkDeleteEntry[]
  deleted?: BulkDeleteEntry[]
  blocked?: BulkDeleteEntry[]
  totals?: CascadeCounts
}

/* bulk_finish_animes 的 skipped reason → 展示文案 */
const FINISH_SKIP_LABELS: Record<string, string> = {
  not_found: '番剧不存在',
  unsupported_type: '类型不支持批量完结（剧场版 / 美漫 / 贤者专区请单条处理）',
  comprehensive_gate: '带综合答题门槛，批量不放行（需单条处理）',
  already_finished: '已是完结旧番',
}

/* 联级项标签：与单条删除组件的 cascadeText 一致 */
const CASCADE_LABELS: [keyof CascadeCounts, string][] = [
  ['favorites', '收藏'],
  ['comments', '评论'],
  ['play_progress', '观看进度'],
  ['episode_feedback', '反馈'],
]

function cascadeTotalsText(counts: CascadeCounts | undefined): string {
  if (!counts) return '无关联用户数据'
  const parts = CASCADE_LABELS.filter(([k]) => (counts[k] ?? 0) > 0).map(
    ([k, label]) => `${label} ${counts[k]} 条`,
  )
  return parts.length > 0 ? parts.join('、') : '无关联用户数据'
}

/* 番剧有实际变更后刷 C 端 cacheTag；全部被跳过时不打这 6 个请求 */
function revalidateAnimeTags(changed: number) {
  if (changed <= 0) return
  for (const tag of ANIME_CACHE_TAGS) {
    void revalidateNextTag(tag)
  }
}

function mapBulkError(message: string): string {
  const msgMap: Record<string, string> = {
    forbidden: '无权限（is_admin 校验未通过）',
    no_ids: '未选择番剧',
    too_many: '超出单次批量上限',
    no_changes: '没有要修改的字段',
    finished_type_missing: '数据库缺少 slug=finished 的分区',
    ongoing_type_missing: '数据库缺少 slug=ongoing 的分区',
    not_found: '番剧不存在',
    invalid: '参数不完整',
  }
  return msgMap[message] || message || '操作失败'
}

async function callBulkDeleteAnimes(
  ids: number[],
  dryRun: boolean,
): Promise<BulkDeleteResult> {
  const { data, error } = await (supabase as any)
    .schema('admin')
    .rpc('bulk_delete_animes', { p_ids: ids, p_dry_run: dryRun })
  if (error) throw error
  return data as BulkDeleteResult
}

/* 被挡条目按 error 分组展示（删除常态是大部分被挡，这是主信息不是边角料） */
const DELETE_BLOCK_GROUP_LABELS: Record<string, string> = {
  has_episodes: '仍有剧集 / 播放地址',
  has_play_urls: '仍有播放地址残留',
  has_merge_redirects: '存在合并重定向（anime_merge_redirects）',
  not_found: '番剧不存在或已被删除',
  forbidden: '无权限',
  invalid: '参数不完整',
}

function BlockedList({ blocked }: { blocked: BulkDeleteEntry[] }) {
  const groups = new Map<string, BulkDeleteEntry[]>()
  for (const b of blocked) {
    const key = b.error ?? 'unknown'
    const list = groups.get(key) ?? []
    list.push(b)
    groups.set(key, list)
  }
  return (
    <Space direction="vertical" size={8}>
      {[...groups.entries()].map(([error, entries]) => (
        <div key={error}>
          <Typography.Text strong>
            {DELETE_BLOCK_GROUP_LABELS[error] ?? error}（{entries.length} 部）
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', fontSize: 12 }}
          >
            {entries
              .map((e) =>
                error === 'has_episodes'
                  ? `#${e.id}（${e.episode_count ?? '?'} 集）`
                  : `#${e.id}`,
              )
              .join('、')}
          </Typography.Text>
        </div>
      ))}
    </Space>
  )
}

function readStoredAnimeListState(): AnimeListState | null {
  try {
    return parseStoredAnimeListState(
      window.localStorage.getItem(ANIME_LIST_STATE_STORAGE_KEY),
    )
  } catch {
    return null
  }
}

function writeStoredAnimeListState(state: AnimeListState) {
  try {
    window.localStorage.setItem(
      ANIME_LIST_STATE_STORAGE_KEY,
      JSON.stringify(toStoredAnimeListState(state)),
    )
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}

/* 移动端没有表头排序：用下拉代替（与桌面表格可排序列一致） */
const MOBILE_SORT_OPTIONS: {
  value: string
  label: string
  field: SortField
  order: SortOrder
}[] = [
  {
    value: 'updated_at:desc',
    label: '最近更新',
    field: 'updated_at',
    order: 'desc',
  },
  {
    value: 'updated_at:asc',
    label: '最早更新',
    field: 'updated_at',
    order: 'asc',
  },
  {
    value: 'created_at:desc',
    label: '最近创建',
    field: 'created_at',
    order: 'desc',
  },
  {
    value: 'created_at:asc',
    label: '最早创建',
    field: 'created_at',
    order: 'asc',
  },
  { value: 'id:desc', label: 'ID 降序', field: 'id', order: 'desc' },
  { value: 'id:asc', label: 'ID 升序', field: 'id', order: 'asc' },
]

/* 集数展示：「当前 / 总」，缺一侧时退化 */
function episodesText(row: AnimeRow): string {
  const cur = row.current_episodes
  const total = row.total_episodes
  return cur != null && total != null
    ? `${cur} / ${total}`
    : cur != null
      ? `${cur}`
      : total != null
        ? `0 / ${total}`
        : '—'
}

function AnimePage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const routeSearch = Route.useSearch()
  const [initialState] = useState<AnimeListState>(() => {
    const rawUrlSearch =
      typeof window === 'undefined'
        ? {}
        : Object.fromEntries(new URLSearchParams(window.location.search))
    const routeState = parseAnimeListSearch(routeSearch)
    if (hasAnimeListSearch(rawUrlSearch)) return routeState
    return readStoredAnimeListState() ?? routeState
  })

  const [page, setPage] = useState(initialState.page)
  const [filters, setFilters] = useState<Filters>(initialState.filters)
  // 输入框草稿：只进 searchInput，Enter/搜索键才落到 filters.search（进 URL/查询）
  const [searchInput, setSearchInput] = useState(initialState.filters.search)
  const [sortBy, setSortBy] = useState<{ field: SortField; order: SortOrder }>({
    field: initialState.sortBy.field,
    order: initialState.sortBy.order,
  })
  const [selectedKeys, setSelectedKeys] = useState<number[]>([])
  const [bulkDeleteChecking, setBulkDeleteChecking] = useState(false)

  // 选择只在当前页/当前筛选语义内有效：翻页、改筛选、改排序都清空
  useEffect(() => {
    setSelectedKeys([])
  }, [page, filters, sortBy])

  const currentState = useMemo<AnimeListState>(
    () => ({ page, filters, sortBy }),
    [filters, page, sortBy],
  )

  useEffect(() => {
    writeStoredAnimeListState(currentState)
    void navigate({
      to: '/anime',
      search: serializeAnimeListSearch(currentState),
      replace: true,
    })
  }, [currentState, navigate])

  const updateFilters = (updater: (filters: Filters) => Filters) => {
    setFilters((f) => updater(f))
    setPage(1)
  }

  const typesQuery = useQuery({
    queryKey: ['anime_types', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('anime_types')
        .select('id, name')
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('name', { ascending: true })
      if (error) throw error
      return data
    },
    staleTime: 5 * 60_000,
  })

  const seriesQuery = useQuery({
    queryKey: ['anime_series', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('anime_series')
        .select('id, title')
        .order('title', { ascending: true })
      if (error) throw error
      return data
    },
    staleTime: 5 * 60_000,
  })

  const listQuery = useQuery({
    queryKey: [
      'animes',
      {
        page,
        search: filters.search,
        is_published: filters.is_published,
        nsfw: filters.nsfw,
        is_recommended: filters.is_recommended,
        type_id: filters.type_id,
        series_id: filters.series_id,
        sortField: sortBy.field,
        sortOrder: sortBy.order,
      },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (page - 1) * PAGE_SIZE
      const to = from + PAGE_SIZE - 1
      const select = '*, anime_types(id, name), anime_series(id, title)'

      // 热度列在 private 快照里，PostgREST 排不了：先由 RPC 按同一组筛选
      // 排好一页 id，再按 id 拉行并恢复顺序
      if (isHeatSortField(sortBy.field)) {
        const { data: idsData, error: idsError } = await (supabase as any)
          .schema('admin')
          .rpc('list_anime_ids_by_heat', {
            p_sort: sortBy.field,
            p_ascending: sortBy.order === 'asc',
            p_search: filters.search.trim() || null,
            p_is_published: filters.is_published ?? null,
            p_nsfw: filters.nsfw ?? null,
            p_is_recommended: filters.is_recommended ?? null,
            p_type_id: filters.type_id ?? null,
            p_series_id: filters.series_id ?? null,
            p_limit: PAGE_SIZE,
            p_offset: from,
          })
        if (idsError) throw idsError
        const r = idsData as HeatSortResult
        if (!r.ok) throw new Error(r.error ?? 'unknown')
        const ids = r.ids ?? []
        if (ids.length === 0) return { rows: [], total: r.total ?? 0 }
        const { data, error } = await supabase
          .from('animes')
          .select(select)
          .in('id', ids)
        if (error) throw error
        const byId = new Map(
          (data as unknown as AnimeRow[]).map((row) => [row.id, row]),
        )
        return {
          rows: ids.flatMap((id) => byId.get(id) ?? []),
          total: r.total ?? 0,
        }
      }

      // 拉全字段：编辑页 几乎用到了 animes 表的每一列（view_count_baseline /
      // aliases / meta_tags / season_* / bangumi_synced_at / dandan_* / pinyin_name 等），
      // 之前手枚举的 select 漏字段会让编辑页读到 undefined。
      let q = supabase
        .from('animes')
        .select(select, { count: 'exact' })
        .order(sortBy.field, {
          ascending: sortBy.order === 'asc',
          nullsFirst: false,
        })
        .range(from, to)
      if (filters.search.trim()) {
        q = q.ilike('search_title', `%${filters.search.trim()}%`)
      }
      if (filters.is_published !== undefined)
        q = q.eq('is_published', filters.is_published)
      if (filters.nsfw !== undefined) q = q.eq('nsfw', filters.nsfw)
      if (filters.is_recommended !== undefined)
        q = q.eq('is_recommended', filters.is_recommended)
      if (filters.type_id !== undefined) q = q.eq('type_id', filters.type_id)
      if (filters.series_id !== undefined)
        q = q.eq('series_id', filters.series_id)
      const { data, error, count } = await q
      if (error) throw error
      return { rows: data as unknown as AnimeRow[], total: count ?? 0 }
    },
  })

  // 热度快照每日 00:20 刷新一次，按当前页 id 补查；不阻塞主表
  const heatIds = useMemo(
    () => (listQuery.data?.rows ?? []).map((r) => r.id),
    [listQuery.data],
  )
  const heatQuery = useQuery({
    queryKey: ['anime_heat', heatIds],
    enabled: heatIds.length > 0,
    staleTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_anime_heat', { p_ids: heatIds })
      if (error) throw error
      const r = data as AnimeHeatResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return {
        asOf: r.as_of ?? null,
        byId: new Map((r.items ?? []).map((h) => [h.anime_id, h])),
      }
    },
  })
  const heatById = heatQuery.data?.byId
  const heatAsOf = heatQuery.data?.asOf ?? null
  // 翻页时旧 Map 里没有新页的 id，占位期间按"加载中"渲染，避免闪 0
  const heatLoaded = heatQuery.isSuccess && !heatQuery.isPlaceholderData

  /* ── 批量操作 mutations ──────────────────────────────────── */

  // 批量标为完结：type_id → 完结旧番 + is_finished（不动 update_weekday /
  // hiatus_until）。被跳过的行按 reason 分组如实展示——需要管理员去单条处理。
  const bulkFinishMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('bulk_finish_animes', { p_ids: ids })
      if (error) throw error
      const r = data as BulkFinishResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r) => {
      const skipped = r.skipped ?? []
      if (skipped.length === 0) {
        message.success(`已完结 ${r.changed ?? 0} / ${r.requested ?? 0} 部`)
      } else {
        const groups = new Map<string, number[]>()
        for (const s of skipped) {
          const list = groups.get(s.reason) ?? []
          list.push(s.id)
          groups.set(s.reason, list)
        }
        modal.info({
          title: `批量完结：${r.changed ?? 0} 部已完结，${skipped.length} 部跳过`,
          content: (
            <Space direction="vertical" size={8} style={{ marginTop: 8 }}>
              {[...groups.entries()].map(([reason, ids]) => (
                <div key={reason}>
                  <Typography.Text strong>
                    {FINISH_SKIP_LABELS[reason] ?? reason}（{ids.length} 部）
                  </Typography.Text>
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 12 }}
                  >
                    {ids.map((id) => `#${id}`).join('、')}
                  </Typography.Text>
                </div>
              ))}
            </Space>
          ),
        })
      }
      void qc.invalidateQueries({ queryKey: ['animes'] })
      setSelectedKeys([])
      revalidateAnimeTags(r.changed ?? 0)
    },
    onError: (e: Error) =>
      message.error(`批量完结失败：${mapBulkError(e.message)}`),
  })

  // 批量发布/转草稿、加/取消推荐：两个 flag 可单传，已是目标态的行后端跳过
  const bulkFlagsMutation = useMutation({
    mutationFn: async (params: {
      ids: number[]
      is_published?: boolean | null
      is_recommended?: boolean | null
      label: string
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('bulk_set_anime_flags', {
          p_ids: params.ids,
          p_is_published: params.is_published ?? null,
          p_is_recommended: params.is_recommended ?? null,
        })
      if (error) throw error
      const r = data as BulkFlagsResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r, vars) => {
      const extras: string[] = []
      if ((r.unchanged?.length ?? 0) > 0)
        extras.push(`${r.unchanged!.length} 部已是目标态`)
      if ((r.not_found?.length ?? 0) > 0)
        extras.push(`${r.not_found!.length} 部不存在`)
      message.success(
        `${vars.label}：已更新 ${r.changed ?? 0} / ${r.requested ?? 0} 部` +
          (extras.length > 0 ? `（${extras.join('，')}）` : ''),
      )
      void qc.invalidateQueries({ queryKey: ['animes'] })
      setSelectedKeys([])
      revalidateAnimeTags(r.changed ?? 0)
    },
    onError: (e: Error) =>
      message.error(`批量更新失败：${mapBulkError(e.message)}`),
  })

  // 批量删除：先 dry_run 拿影响面 → Modal.confirm 展示可删/被挡/级联计数 →
  // 确认后才真删。硬约束由后端把关，前端把被挡原因分组透出。
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => callBulkDeleteAnimes(ids, false),
    onSuccess: (r) => {
      const deleted = r.deleted ?? []
      const blocked = r.blocked ?? []
      if (blocked.length === 0) {
        message.success(`已删除 ${deleted.length} 部番剧`)
      } else {
        modal.info({
          title: `批量删除：已删 ${deleted.length} 部，${blocked.length} 部被挡`,
          content: <BlockedList blocked={blocked} />,
        })
      }
      void qc.invalidateQueries({ queryKey: ['animes'] })
      setSelectedKeys([])
      revalidateAnimeTags(deleted.length)
    },
    onError: (e: Error) =>
      message.error(`批量删除失败：${mapBulkError(e.message)}`),
  })

  const bulkBusy =
    bulkFinishMutation.isPending ||
    bulkFlagsMutation.isPending ||
    bulkDeleteMutation.isPending ||
    bulkDeleteChecking

  const handleBulkFinish = () => {
    if (selectedKeys.length === 0) return
    modal.confirm({
      title: `批量标为完结（${selectedKeys.length} 部）？`,
      content: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          类型转为「完结旧番」并标记 is_finished；不影响更新星期与停播标记。
          剧场版 / 美漫 / 贤者专区及带答题门槛的番剧会被跳过。
        </Typography.Text>
      ),
      okText: '执行',
      cancelText: '取消',
      onOk: () =>
        bulkFinishMutation
          .mutateAsync(selectedKeys)
          .then(() => undefined)
          .catch(() => undefined),
    })
  }

  const handleBulkDelete = async () => {
    if (selectedKeys.length === 0) return
    if (selectedKeys.length > BULK_DELETE_CAP) {
      message.error(`一次最多删除 ${BULK_DELETE_CAP} 部`)
      return
    }
    setBulkDeleteChecking(true)
    try {
      const r = await callBulkDeleteAnimes(selectedKeys, true)
      if (!r.ok) {
        modal.error({
          title: '无法批量删除',
          content: mapBulkError(r.error ?? 'unknown'),
        })
        return
      }
      const eligible = r.eligible ?? []
      const blocked = r.blocked ?? []
      if (eligible.length === 0) {
        modal.info({
          title: '没有可删除的番剧',
          content: <BlockedList blocked={blocked} />,
        })
        return
      }
      modal.confirm({
        title: `批量删除 ${eligible.length} 部番剧？`,
        width: 560,
        content: (
          <Space direction="vertical" size={8} style={{ marginTop: 8 }}>
            <div>
              <Typography.Text strong>
                将删除（{eligible.length} 部）
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ display: 'block', fontSize: 12 }}
              >
                {eligible
                  .slice(0, 20)
                  .map((e) => `《${e.title ?? e.id}》`)
                  .join('、')}
                {eligible.length > 20 ? ` 等 ${eligible.length} 部` : ''}
              </Typography.Text>
            </div>
            {blocked.length > 0 ? <BlockedList blocked={blocked} /> : null}
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              将联级清除：{cascadeTotalsText(r.totals)}。此操作不可恢复。
            </Typography.Text>
          </Space>
        ),
        okText: '确认删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: () =>
          bulkDeleteMutation
            .mutateAsync(selectedKeys)
            .then(() => undefined)
            .catch(() => undefined),
      })
    } catch (err) {
      message.error(`预检查失败：${(err as Error).message}`)
    } finally {
      setBulkDeleteChecking(false)
    }
  }

  const sortOrderFor = (field: SortField) =>
    sortBy.field === field
      ? sortBy.order === 'asc'
        ? ('ascend' as const)
        : ('descend' as const)
      : null

  const columns: ColumnsType<AnimeRow> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        width: 80,
        sorter: true,
        sortOrder: sortOrderFor('id'),
        render: (id: number) => (
          <Typography.Text
            style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}
            type="secondary"
          >
            {id}
          </Typography.Text>
        ),
      },
      {
        title: '标题',
        dataIndex: 'title',
        minWidth: 300,
        render: (_: unknown, row) => (
          <Space size={12} align="center">
            {row.cover_url ? (
              <Image
                src={row.cover_url}
                alt=""
                width={40}
                height={56}
                style={{ objectFit: 'cover', borderRadius: 6 }}
                preview={{ mask: false }}
                placeholder
              />
            ) : (
              <div
                className="admin-cell-media"
                style={{
                  width: 40,
                  height: 56,
                  borderRadius: 6,
                  background: 'var(--ant-color-fill-tertiary)',
                }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <Typography.Text strong ellipsis style={{ display: 'block' }}>
                {row.title}
              </Typography.Text>
              {row.title_original ? (
                <Typography.Text
                  type="secondary"
                  ellipsis
                  style={{ display: 'block', fontSize: 12, marginTop: 2 }}
                >
                  {row.title_original}
                </Typography.Text>
              ) : null}
            </div>
          </Space>
        ),
      },
      {
        title: '类型',
        width: 100,
        render: (_: unknown, row) =>
          row.anime_types?.name ?? (
            <span style={{ color: 'var(--ant-color-text-quaternary)' }}>—</span>
          ),
      },
      {
        title: '系列',
        width: 140,
        render: (_: unknown, row) =>
          row.anime_series?.title ?? (
            <span style={{ color: 'var(--ant-color-text-quaternary)' }}>—</span>
          ),
      },
      {
        title: '状态',
        width: 190,
        render: (_: unknown, row) => (
          <Space size={4} wrap>
            <Tag color={row.is_published ? 'success' : 'default'}>
              {row.is_published ? '已发布' : '草稿'}
            </Tag>
            {row.is_recommended ? <Tag color="processing">推荐</Tag> : null}
            {row.nsfw ? <Tag color="error">NSFW</Tag> : null}
            {isAnimeHiatusActive(row.hiatus_until) ? (
              <Tag
                color="warning"
                title={`停播至 ${formatCST(row.hiatus_until)}`}
              >
                停播至 {formatCST(row.hiatus_until, 'MM-DD')}
              </Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: '集数',
        width: 110,
        render: (_: unknown, row) => {
          const fin = row.is_finished
          const display = episodesText(row)
          return (
            <Link
              to="/anime/$animeId/episodes"
              params={{ animeId: String(row.id) }}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 6,
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--ant-color-link)',
              }}
              title="管理剧集"
            >
              <span>{display}</span>
              {fin ? (
                <Typography.Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    color: 'var(--ant-color-text-tertiary)',
                  }}
                >
                  完结
                </Typography.Text>
              ) : null}
            </Link>
          )
        },
      },
      {
        title: (
          <Tooltip title="累计播放（含旧站导入的基数）；灰字为本站上线后的净增">
            总播放
          </Tooltip>
        ),
        dataIndex: 'view_count',
        width: 110,
        align: 'right',
        sorter: true,
        sortOrder: sortOrderFor('view_count'),
        render: (vc: number | null, row) => {
          const total = vc ?? 0
          const net = Math.max(total - (row.view_count_baseline ?? 0), 0)
          return (
            <div
              style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}
              title={`累计 ${total.toLocaleString('zh-CN')} · 站内净增 ${net.toLocaleString('zh-CN')}`}
            >
              <div>{formatCount(total)}</div>
              {row.view_count_baseline ? (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  +{formatCount(net)}
                </Typography.Text>
              ) : null}
            </div>
          )
        },
      },
      ...(
        [
          ['views_yesterday', '昨日', 80],
          ['views_7d', '近 7 日', 110],
          ['views_30d', '近 30 日', 90],
        ] as const
      ).map(([key, label, width]) => ({
        key,
        dataIndex: key,
        sorter: true,
        sortOrder: sortOrderFor(key),
        title: (
          <Tooltip
            title={
              heatAsOf
                ? `按自然日统计，截至 ${heatAsOf}（每日 00:20 更新）`
                : '按自然日统计，每日 00:20 更新'
            }
          >
            {label}
          </Tooltip>
        ),
        width,
        align: 'right' as const,
        render: (_: unknown, row: AnimeRow) => {
          if (!heatLoaded) {
            return <Typography.Text type="secondary">…</Typography.Text>
          }
          const h = heatById?.get(row.id) ?? EMPTY_HEAT
          const v = h[key]
          const wow =
            key === 'views_7d'
              ? weekOverWeek(h.views_7d, h.views_prev_7d)
              : null
          return (
            <span
              style={{ fontVariantNumeric: 'tabular-nums' }}
              title={
                key === 'views_7d'
                  ? `近 7 日 ${h.views_7d.toLocaleString('zh-CN')} · 前 7 日 ${h.views_prev_7d.toLocaleString('zh-CN')}`
                  : v.toLocaleString('zh-CN')
              }
            >
              <Typography.Text type={v === 0 ? 'secondary' : undefined}>
                {formatCount(v)}
              </Typography.Text>
              {wow ? (
                <Typography.Text
                  type={wow.up ? 'success' : 'danger'}
                  style={{ fontSize: 11, marginInlineStart: 4 }}
                >
                  {wow.text}
                </Typography.Text>
              ) : null}
            </span>
          )
        },
      })),
      {
        title: '创建',
        dataIndex: 'created_at',
        width: 120,
        sorter: true,
        sortOrder: sortOrderFor('created_at'),
        render: (t: string | null) =>
          t ? (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12 }}
              title={formatCST(t)}
            >
              {formatCST(t, 'YYYY-MM-DD')}
            </Typography.Text>
          ) : (
            '—'
          ),
      },
      {
        title: '更新',
        dataIndex: 'updated_at',
        width: 120,
        sorter: true,
        sortOrder: sortOrderFor('updated_at'),
        render: (t: string | null) =>
          t ? (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12 }}
              title={formatCST(t)}
            >
              {formatCST(t, 'YYYY-MM-DD')}
            </Typography.Text>
          ) : (
            '—'
          ),
      },
      {
        title: '操作',
        width: 240,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={0}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                navigate({
                  to: '/anime/$animeId/edit',
                  params: { animeId: String(row.id) },
                })
              }}
            >
              元数据
            </Button>
            <Button
              type="link"
              size="small"
              icon={<OrderedListOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                navigate({
                  to: '/anime/$animeId/episodes',
                  params: { animeId: String(row.id) },
                })
              }}
            >
              剧集
            </Button>
            {/* 停播只对正连载中的番剧开放；完结番没有"本周不更"的概念 */}
            {row.is_finished ? null : (
              <AnimeHiatusAction
                animeId={row.id}
                hiatusUntil={row.hiatus_until}
                updateWeekday={row.update_weekday}
              />
            )}
          </Space>
        ),
      },
    ],
    [sortBy.field, sortBy.order, heatById, heatAsOf, heatLoaded],
  )

  const searchBox = (
    <Input.Search
      allowClear
      placeholder="搜索标题 / 别名"
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
      onSearch={(v) => updateFilters((f) => ({ ...f, search: v.trim() }))}
      style={{ width: 240 }}
    />
  )

  // 次要筛选：桌面与搜索框同一行流式排列；手机折叠进「筛选」面板
  const secondaryFilters = (
    <>
      <Select
        allowClear
        placeholder="发布状态"
        style={{ width: 130 }}
        value={filters.is_published}
        onChange={(v: boolean | undefined) =>
          updateFilters((f) => ({ ...f, is_published: v }))
        }
        options={[
          { value: true, label: '已发布' },
          { value: false, label: '草稿' },
        ]}
      />
      <Select
        allowClear
        placeholder="NSFW"
        style={{ width: 110 }}
        value={filters.nsfw}
        onChange={(v: boolean | undefined) =>
          updateFilters((f) => ({ ...f, nsfw: v }))
        }
        options={[
          { value: true, label: 'NSFW' },
          { value: false, label: '非 NSFW' },
        ]}
      />
      <Select
        allowClear
        placeholder="推荐位"
        style={{ width: 120 }}
        value={filters.is_recommended}
        onChange={(v: boolean | undefined) =>
          updateFilters((f) => ({
            ...f,
            is_recommended: v,
          }))
        }
        options={[
          { value: true, label: '已推荐' },
          { value: false, label: '未推荐' },
        ]}
      />
      <Select
        allowClear
        showSearch
        placeholder="类型"
        style={{ width: 160 }}
        value={filters.type_id}
        onChange={(v: number | undefined) =>
          updateFilters((f) => ({ ...f, type_id: v }))
        }
        options={typesQuery.data?.map((t) => ({
          value: t.id,
          label: t.name,
        }))}
        loading={typesQuery.isLoading}
        filterOption={(input, option) =>
          String(option?.label ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />
      <Select
        allowClear
        showSearch
        placeholder="系列"
        style={{ width: 200 }}
        value={filters.series_id}
        onChange={(v: number | undefined) =>
          updateFilters((f) => ({ ...f, series_id: v }))
        }
        options={seriesQuery.data?.map((s) => ({
          value: s.id,
          label: s.title,
        }))}
        loading={seriesQuery.isLoading}
        filterOption={(input, option) =>
          String(option?.label ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />
    </>
  )
  // 手机批量条：「标为完结」露出，其余收进「更多」
  const mobileBulkMenu: MenuProps = {
    items: [
      { key: 'recommend', icon: <StarOutlined />, label: '设为推荐' },
      { key: 'unrecommend', label: '取消推荐' },
      { key: 'publish', label: '发布' },
      { key: 'draft', label: '转为草稿' },
      { type: 'divider' },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
        disabled: selectedKeys.length > BULK_DELETE_CAP,
        label: `删除（上限 ${BULK_DELETE_CAP} 部）`,
      },
    ],
    onClick: ({ key }) => {
      if (key === 'delete') {
        void handleBulkDelete()
        return
      }
      const flags: Record<
        string,
        { is_published?: boolean; is_recommended?: boolean; label: string }
      > = {
        recommend: { is_recommended: true, label: '设为推荐' },
        unrecommend: { is_recommended: false, label: '取消推荐' },
        publish: { is_published: true, label: '发布' },
        draft: { is_published: false, label: '转为草稿' },
      }
      const f = flags[key]
      if (f) bulkFlagsMutation.mutate({ ids: selectedKeys, ...f })
    },
  }

  const pageRows = listQuery.data?.rows ?? []
  const allPageSelected =
    pageRows.length > 0 && pageRows.every((r) => selectedKeys.includes(r.id))
  const toggleSelected = (id: number, checked: boolean) =>
    setSelectedKeys((keys) =>
      checked ? [...keys, id] : keys.filter((k) => k !== id),
    )
  const openEdit = (row: AnimeRow) =>
    navigate({
      to: '/anime/$animeId/edit',
      params: { animeId: String(row.id) },
    })

  const secondaryFilterCount = [
    filters.is_published,
    filters.nsfw,
    filters.is_recommended,
    filters.type_id,
    filters.series_id,
  ].filter((v) => v !== undefined).length

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-header__title">番剧</h1>
          <div className="admin-page-header__meta">
            共 {(listQuery.data?.total ?? 0).toLocaleString('zh-CN')} 部
            {listQuery.data
              ? ` · 当前页 ${listQuery.data.rows.length} 条`
              : ' · 加载中'}
          </div>
        </div>
        <Space>
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => listQuery.refetch()}
              loading={listQuery.isFetching && !listQuery.isLoading}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate({ to: '/anime-new' })}
          >
            新建番剧
          </Button>
        </Space>
      </div>

      <Card className="admin-table-card anime-list-card">
        <div className="admin-table-card__filters">
          {isMobile ? (
            <AdminFilterBar
              primary={searchBox}
              activeCount={secondaryFilterCount}
            >
              {secondaryFilters}
            </AdminFilterBar>
          ) : (
            <>
              {searchBox}
              {secondaryFilters}
            </>
          )}
        </div>

        {/* 批量操作条：选中 ≥1 时出现；选择只在当前页有效 */}
        {!isMobile && selectedKeys.length > 0 ? (
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              background: 'var(--ant-color-primary-bg)',
              border: '1px solid var(--ant-color-primary-border)',
              borderRadius: 6,
              padding: '8px 12px',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <Typography.Text strong>
              已选 {selectedKeys.length} 部
              {selectedKeys.length > BULK_DELETE_CAP ? (
                <Typography.Text type="danger" style={{ marginLeft: 8 }}>
                  （删除上限 {BULK_DELETE_CAP} 部）
                </Typography.Text>
              ) : null}
            </Typography.Text>
            <Space size={4} wrap>
              <Button
                size="small"
                icon={<CheckOutlined />}
                onClick={handleBulkFinish}
                disabled={bulkBusy}
              >
                标为完结
              </Button>
              <Button
                size="small"
                icon={<StarOutlined />}
                onClick={() =>
                  bulkFlagsMutation.mutate({
                    ids: selectedKeys,
                    is_recommended: true,
                    label: '设为推荐',
                  })
                }
                disabled={bulkBusy}
              >
                设为推荐
              </Button>
              <Button
                size="small"
                onClick={() =>
                  bulkFlagsMutation.mutate({
                    ids: selectedKeys,
                    is_recommended: false,
                    label: '取消推荐',
                  })
                }
                disabled={bulkBusy}
              >
                取消推荐
              </Button>
              <Button
                size="small"
                onClick={() =>
                  bulkFlagsMutation.mutate({
                    ids: selectedKeys,
                    is_published: true,
                    label: '发布',
                  })
                }
                disabled={bulkBusy}
              >
                发布
              </Button>
              <Button
                size="small"
                onClick={() =>
                  bulkFlagsMutation.mutate({
                    ids: selectedKeys,
                    is_published: false,
                    label: '转为草稿',
                  })
                }
                disabled={bulkBusy}
              >
                转为草稿
              </Button>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={bulkDeleteChecking || bulkDeleteMutation.isPending}
                onClick={handleBulkDelete}
                disabled={bulkBusy || selectedKeys.length > BULK_DELETE_CAP}
              >
                删除
              </Button>
            </Space>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, marginLeft: 'auto' }}
            >
              仅当前页 · 删除单次上限 {BULK_DELETE_CAP} 部
            </Typography.Text>
            <Button
              type="link"
              size="small"
              onClick={() => setSelectedKeys([])}
              style={{ padding: 0 }}
            >
              清除选择
            </Button>
          </div>
        ) : null}

        <Table<AnimeRow>
          className="admin-table-wrap"
          rowKey="id"
          size="small"
          loading={listQuery.isLoading}
          columns={columns}
          dataSource={listQuery.data?.rows ?? []}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys as number[]),
            columnWidth: 44,
          }}
          onRow={(row) => ({
            onClick: (e) => {
              // 选择列里的点击（checkbox / 单元格）不触发行跳转
              const target = e.target as HTMLElement
              if (
                target.closest('.ant-table-selection-column') ||
                target.closest('input[type="checkbox"]')
              ) {
                return
              }
              navigate({
                to: '/anime/$animeId/edit',
                params: { animeId: String(row.id) },
              })
            },
            style: { cursor: 'pointer' },
          })}
          scroll={{ x: 1680 }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: listQuery.data?.total ?? 0,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条`,
          }}
          onChange={(pagination, _filters, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter
            let nextSort: {
              field: SortField
              order: SortOrder
            } = { field: 'updated_at', order: 'desc' }
            if (s.field && s.order) {
              const field = String(s.field)
              if (
                field === 'id' ||
                field === 'created_at' ||
                field === 'updated_at' ||
                field === 'view_count' ||
                isHeatSortField(field)
              ) {
                nextSort = {
                  field,
                  order: s.order === 'ascend' ? 'asc' : 'desc',
                }
              }
            }

            if (
              nextSort.field !== sortBy.field ||
              nextSort.order !== sortBy.order
            ) {
              setSortBy(nextSort)
              setPage(1)
            } else if (pagination.current && pagination.current !== page) {
              setPage(pagination.current)
            }
          }}
        />
        {isMobile ? (
          <div className="anime-mobile">
            <div className="anime-mobile__toolbar">
              <Checkbox
                checked={allPageSelected}
                indeterminate={selectedKeys.length > 0 && !allPageSelected}
                disabled={pageRows.length === 0}
                onChange={(e) =>
                  setSelectedKeys(
                    e.target.checked ? pageRows.map((r) => r.id) : [],
                  )
                }
              >
                全选本页
              </Checkbox>
              <Select
                size="small"
                aria-label="排序"
                className="anime-mobile__sort"
                value={`${sortBy.field}:${sortBy.order}`}
                options={MOBILE_SORT_OPTIONS.map(({ value, label }) => ({
                  value,
                  label,
                }))}
                onChange={(v: string) => {
                  const o = MOBILE_SORT_OPTIONS.find((x) => x.value === v)
                  if (!o) return
                  setSortBy({ field: o.field, order: o.order })
                  setPage(1)
                }}
              />
            </div>
            <AdminMobileList<AnimeRow>
              rows={pageRows}
              rowKey={(row) => row.id}
              loading={listQuery.isLoading}
              page={page}
              pageSize={PAGE_SIZE}
              total={listQuery.data?.total ?? 0}
              onPageChange={setPage}
              renderItem={(row) => {
                const selected = selectedKeys.includes(row.id)
                const hiatus = isAnimeHiatusActive(row.hiatus_until)
                return (
                  <article
                    className={[
                      'admin-mobile-record',
                      'anime-mobile-card',
                      selected ? 'anime-mobile-card--selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="button"
                    tabIndex={0}
                    aria-label={`编辑《${row.title}》元数据`}
                    onClick={() => openEdit(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        openEdit(row)
                      }
                    }}
                  >
                    <div className="admin-mobile-record__lead">
                      {row.cover_url ? (
                        <img
                          className="anime-mobile-card__cover"
                          src={row.cover_url}
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        <div className="anime-mobile-card__cover" />
                      )}
                      <div className="anime-mobile-card__main">
                        <div className="admin-mobile-record__head">
                          <div className="admin-mobile-record__title anime-mobile-card__title">
                            {row.title}
                          </div>
                          <Tag
                            color={row.is_published ? 'success' : 'default'}
                            style={{ marginInlineEnd: 0, flex: 'none' }}
                          >
                            {row.is_published ? '已发布' : '草稿'}
                          </Tag>
                        </div>
                        {row.title_original ? (
                          <div className="anime-mobile-card__sub">
                            {row.title_original}
                          </div>
                        ) : null}
                        <div className="admin-mobile-record__meta">
                          <span>{row.anime_types?.name ?? '未分类'}</span>
                          {row.anime_series ? (
                            <span>系列 {row.anime_series.title}</span>
                          ) : null}
                          <span className="anime-mobile-card__num">
                            {episodesText(row)} 集
                            {row.is_finished ? ' · 完结' : ''}
                          </span>
                          {heatLoaded ? (
                            <span className="anime-mobile-card__num">
                              近7日{' '}
                              {formatCount(
                                heatById?.get(row.id)?.views_7d ?? 0,
                              )}
                            </span>
                          ) : null}
                          {row.updated_at ? (
                            <span title={formatCST(row.updated_at)}>
                              更新 {formatCST(row.updated_at, 'MM-DD')}
                            </span>
                          ) : null}
                        </div>
                        {row.is_recommended || row.nsfw || hiatus ? (
                          <div className="admin-mobile-record__badges">
                            {row.is_recommended ? (
                              <Tag color="processing">推荐</Tag>
                            ) : null}
                            {row.nsfw ? <Tag color="error">NSFW</Tag> : null}
                            {hiatus ? (
                              <Tag color="warning">
                                停播至 {formatCST(row.hiatus_until, 'MM-DD')}
                              </Tag>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div
                      className="admin-mobile-record__actions"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <span className="admin-mobile-record__leading">
                        <Checkbox
                          checked={selected}
                          onChange={(e) =>
                            toggleSelected(row.id, e.target.checked)
                          }
                        >
                          <span className="anime-mobile-card__num">
                            #{row.id}
                          </span>
                        </Checkbox>
                      </span>
                      {/* 停播只对正连载中的番剧开放 */}
                      {row.is_finished ? null : (
                        <AnimeHiatusAction
                          animeId={row.id}
                          hiatusUntil={row.hiatus_until}
                          updateWeekday={row.update_weekday}
                          buttonType="default"
                        />
                      )}
                      <Button
                        type="primary"
                        ghost
                        icon={<OrderedListOutlined />}
                        onClick={() =>
                          navigate({
                            to: '/anime/$animeId/episodes',
                            params: { animeId: String(row.id) },
                          })
                        }
                      >
                        剧集
                      </Button>
                    </div>
                  </article>
                )
              }}
            />
            {selectedKeys.length > 0 ? (
              <div className="admin-mobile-bulkbar">
                <span className="admin-mobile-bulkbar__count">
                  已选 {selectedKeys.length} 部
                </span>
                <Button
                  icon={<CheckOutlined />}
                  onClick={handleBulkFinish}
                  disabled={bulkBusy}
                >
                  标为完结
                </Button>
                <Dropdown
                  menu={mobileBulkMenu}
                  trigger={['click']}
                  placement="topRight"
                  disabled={bulkBusy}
                >
                  <Button
                    icon={<MoreOutlined />}
                    loading={bulkDeleteChecking || bulkDeleteMutation.isPending}
                  >
                    更多
                  </Button>
                </Dropdown>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  aria-label="取消选择"
                  onClick={() => setSelectedKeys([])}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>
    </div>
  )
}
